import {Box} from '@jobseeker/lotus/foundations';
import {Button, capitalize, Grid, Skeleton, Typography} from '@mui/material';
import {useEffect, useState} from 'react';
import {shallowEqual, useSelector} from 'react-redux';
import {interviewService} from '@jobseeker/common-core/services';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import {AppState} from '@jobseeker/common-core/types';
import {Image} from '@jobseeker/lotus/components/Image';
import {BannerIcon} from '@jobseeker/lotus/assets/icons';
import Calendar from './Calendar';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import PostSlotConfirmationDialog from './PostSlotConfirmationDialog';
import HiristBannerComponent from '@jobseeker/lotus/components/HiristBanner';
import {enqueueSnackbar} from 'notistack';

dayjs.extend(isSameOrBefore);

const InterviewDateAndSlotPicker = ({
  slotId,
  calendarId,
  handleFlowPostSlotConfirmation,
}: {
  slotId: string;
  calendarId: string;
  handleFlowPostSlotConfirmation: () => void;
}) => {
  const [showPostSlotConfirmationDialog, setShowPostSlotConfirmationDialog] =
    useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [slotDetails, setSlotDetails] = useState<{
    slotId: string;
    calendarId: string;
    address: string;
    messageTelephonic: string;
    inviteStatus: number;
    slotType: string;
    recruiterId: number;
    slotDate: string;
    slotTime: string;
    status: number;
    jobInfo: {
      id: number;
      title: string;
      minExp: number;
      maxExp: number;
      locations: string;
      confidential: number;
    };
    recruiterInfo: {
      name: string;
      organization: string;
      designation: string;
      profileLink: string;
      imgUrl: string;
      companyId: number;
    };
    formattedDate: string;
    calendarLinks: {
      google: string;
      yahoo: string;
      outlook: string;
    };
  }>();

  const {device} = useSelector(
    (state: AppState) => state.app.layout,
    shallowEqual
  );

  const WINDOW_SIZE = device === 'mobile' ? 1 : 5;
  const isAboveLarge = useIsLargeDevice('md');

  const [remainingSlotsCount, setRemainingSlotsCount] = useState(Infinity);
  const [message, setMessage] = useState('');
  const [fromDate, setFromDate] = useState<Date>(new Date());
  const [toDate, setToDate] = useState<Date>(
    dayjs().add(WINDOW_SIZE, 'days').toDate()
  );

  const urlSearchParams = new URLSearchParams(
    typeof window !== 'undefined' ? window.location.search : ''
  );
  const reschedule = urlSearchParams.get('reschedule') ?? '0';

  useEffect(() => {
    (async () => {
      const [countData, data] = await Promise.all([
        interviewService.getSlotsCount({
          calendarId,
          slotId,
        }),
        interviewService.getSlotDetails({
          calendarId,
          slotId,
          from: fromDate.toISOString(),
        }),
      ]);

      if (!data) {
        return;
      }

      if (data?.error) {
        const {message = ''} = data.error;

        // Handle the case where this user isn't the same as the user who booked this slot
        if (message?.startsWith('This interview')) {
          setMessage(message);
        }

        return;
      }

      if (!data?.data) {
        return;
      }

      setSlotDetails(data.data);

      if (data?.data?.inviteStatus === 2 && reschedule !== '1') {
        setShowPostSlotConfirmationDialog(true);
      }

      if (!countData || !countData?.count || countData.count === 0) {
        setRemainingSlotsCount(0);
        return;
      }

      setIsLoading(false);

      setRemainingSlotsCount(data.data?.remainingSlotsCount);
    })();
  }, [
    calendarId,
    slotId,
    fromDate,
    toDate,
    reschedule,
    handleFlowPostSlotConfirmation,
  ]);

  const handleDateChange = (date: Date, substract = true) => {
    if (substract) {
      date = dayjs(date).subtract(5.5, 'hours').toDate();
    }

    if (dayjs(date).isSameOrBefore(dayjs(), 'days')) {
      date = new Date();
    }

    setFromDate(date);
    setToDate(dayjs(date).add(WINDOW_SIZE, 'days').toDate());
  };

  const notifyRecruiterAboutNoAvailableSlots = async () => {
    try {
      const response =
        await interviewService.notifyRecruiterAboutNoAvailableSlots({
          slotId,
          jobId: slotDetails?.jobInfo.id || Infinity,
          recruiterId: slotDetails?.recruiterId || Infinity,
        });

      enqueueSnackbar(response.message, {
        variant: response.success === 1 ? 'success' : 'error',
      });
    } catch (err) {
      enqueueSnackbar('Something went wrong. Please try again later!', {
        variant: 'error',
      });
    }
  };

  if (message.length) {
    return <>{message}</>;
  }

  const title = slotDetails?.jobInfo?.title?.split(' (')[0];

  if (remainingSlotsCount <= 0 && !showPostSlotConfirmationDialog) {
    return (
      <>
        <HiristBannerComponent>
          {' '}
          <>
            <Box
              width={88}
              mr={4}
              alignSelf={isAboveLarge ? 'center' : 'start'}
              sx={isAboveLarge ? {} : {mt: 3}}
            >
              <Image
                src={BannerIcon}
                alt="banner-icon"
                width={isAboveLarge ? 70 : 50}
                height={isAboveLarge ? 70 : 50}
              />
            </Box>
            <Box alignSelf="center">
              {isAboveLarge && (
                <Typography color="white" fontSize={16} sx={{color: '#ffbe28'}}>
                  Congratulations!
                </Typography>
              )}
              <Typography color="white" fontSize={17} fontWeight={700}>
                You have been shortlisted for{' '}
                {capitalize(slotDetails?.slotType ?? '')} interview for:
              </Typography>
              <Typography
                color="white"
                fontSize={isAboveLarge ? 22 : 16}
                maxWidth={733}
                fontWeight={700}
              >
                {title}
              </Typography>
            </Box>
          </>
        </HiristBannerComponent>
        <Box textAlign="center" py={5} mt={5}>
          <Image
            src="https://job-static.hirist.com/V8/static/media/no-slot-icon.81463dec.svg"
            alt="No Slots Available"
            height={183}
            width={209}
          />
          <Typography fontSize={32} fontWeight={400}>
            Oops!
          </Typography>
          <Typography fontSize={22} fontWeight={300} mb={4}>
            Currently no interview slots are available, would you like to notify
            the recruiter?
          </Typography>
          <Button
            variant="contained"
            onClick={notifyRecruiterAboutNoAvailableSlots}
          >
            Inform Recruiter
          </Button>
        </Box>
      </>
    );
  }

  return (
    <>
      <PostSlotConfirmationDialog
        slotDetails={slotDetails}
        open={showPostSlotConfirmationDialog}
      />
      <HiristBannerComponent>
        {' '}
        <>
          <Box
            width={88}
            mr={4}
            alignSelf={isAboveLarge ? 'center' : 'start'}
            sx={isAboveLarge ? {} : {mt: 3}}
          >
            <Image
              src={BannerIcon}
              alt="banner-icon"
              width={isAboveLarge ? 70 : 50}
              height={isAboveLarge ? 70 : 50}
            />
          </Box>
          <Box alignSelf="center">
            {isAboveLarge && (
              <Typography color="white" fontSize={16} sx={{color: '#ffbe28'}}>
                Congratulations!
              </Typography>
            )}
            <Typography color="white" fontSize={17} fontWeight={700}>
              You have been shortlisted for{' '}
              {capitalize(slotDetails?.slotType ?? '')} interview for:
            </Typography>
            <Typography
              color="white"
              fontSize={isAboveLarge ? 22 : 16}
              maxWidth={733}
              fontWeight={700}
            >
              {title}
            </Typography>
          </Box>
        </>
      </HiristBannerComponent>
      {isLoading ? (
        <Box
          borderRadius="16px"
          sx={{background: '#FFF'}}
          boxShadow="0px 6.48px 6px 2px rgba(41, 41, 41, 0.04)"
          p={2}
        >
          <Skeleton variant="text" width="60%" height={30} sx={{mb: 2}} />

          <Grid container mt={1.5} mb={4} gap={1} alignItems="flex-start">
            <Grid item>
              <Skeleton
                variant="rectangular"
                width={50}
                height={50}
                sx={{borderRadius: '6px'}}
              />
            </Grid>

            <Grid item xs>
              <Grid container direction="column" gap={{xxs: 1, md: 1.5}}>
                <Grid item>
                  <Skeleton variant="text" width="70%" height={20} />
                </Grid>

                <Grid item>
                  <Skeleton variant="text" width="50%" height={18} />
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Box display="flex" flexDirection="row" gap={2} mt={2}>
            {[...Array(4)].map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                width="22%"
                height={40}
                sx={{borderRadius: '8px'}}
              />
            ))}
          </Box>

          <Skeleton variant="rounded" width="100%" height={40} sx={{my: 5}} />

          <Box mt={3}>
            {[...Array(2)].map((_, index) => (
              <Skeleton
                key={index}
                variant="rounded"
                width="100%"
                height={40}
                sx={{my: 2}}
              />
            ))}
          </Box>
        </Box>
      ) : (
        <Calendar
          fromDate={fromDate}
          toDate={toDate}
          slotId={slotId}
          calendarId={calendarId}
          reschedule={parseInt(reschedule, 10)}
          handleDateChange={handleDateChange}
          recruiterId={slotDetails?.recruiterId}
        />
      )}
    </>
  );
};

export default InterviewDateAndSlotPicker;
